<template>
	<div class="optional-items">
		<div @click="$refs.optionalSettingsDialog.open()"
			style="background: #fafafa; border-bottom: 1px solid #eee; border-top: 1px solid #eee; color: gray; font-size: smaller; margin-bottom: 20px; padding: 8px 24px; margin: -8px -24px 20px -24px; cursor: pointer;"
		>
			<v-icon>mdi-cog</v-icon>
			{{ $t('text.additionalSettingsCustomize') }}
		</div>
		<Optional id="validitySettings" v-model="optionalSettings" :autoShow="true">
			<FieldSet id="validitySettings" required>
				<Field typeName="AddedValue" fieldName="validityType" v-model="modelValue.fields.validityType" />
				<div style="display: flex; gap: 10px;" :class="{
					disabled: modelValue.fields.validityType?.de !== 'variable+',
				}">
					<Field typeName="AddedValue" fieldName="validDaysBefore" v-model="modelValue.fields.validDaysBefore" style="flex: 1;" />
					<Field typeName="AddedValue" fieldName="validDaysAfter" v-model="modelValue.fields.validDaysAfter" style="flex: 1;" />
				</div>
			</FieldSet>
		</Optional>

		<Optional id="seasonalDiscounts" v-model="optionalSettings" :autoShow="!!modelValue.fields.seasonalDiscounts?.de?.length">
			<FieldSet id="seasonalDiscounts" class="pt-6">
				<AddButton
					@click="addSeasonalDiscount"
					buttonText="seasonalDiscount"
				/>
				<EditableTable
					class="my-4"
					v-model="modelValue.fields.seasonalDiscounts.de"
					:columns="[ 'startDate', 'endDate', 'reductionPercent' ]"
					typeId="SeasonalDiscount"
					no-data-text="noSeasonalDiscountsSet"
				>
					<template #deleteItem="{ item }">
						<v-icon class="deleteIcon" @click="(action) => handleOptionClick(item.sys.id, 'delete')">mdi-delete</v-icon>
					</template>
				</EditableTable>
			</FieldSet>
		</Optional>

		<!-- TODO: extend field to support an array field that adds elements when pressing enter and shows them as tags -->
		<Optional id="arrivalAndDepartureRestrictions" v-model="optionalSettings"
			:autoShow="true"
		>
			<FieldSet id="restrictions">				
				<!-- Static field - always shown based on journey type -->
				<div class="mb-4">
					<!-- For outward journey: always show arrival stations -->
					<div v-if="isOutwardJourney">
						<Field 
							typeName="AddedValue" 
							fieldName="arrivalStations" 
							v-model="modelValue.fields.arrivalStations"
							:placeholder="$t('text.enterStationIds')"
							:info-text="$t('text.specifyStationIdsLimitDestinations')"
	
						/>
					</div>
					
					<!-- For return journey: always show departure stations -->
					<div v-else>
						<Field 
							typeName="AddedValue" 
							fieldName="departureStations" 
							v-model="modelValue.fields.departureStations"
							:placeholder="$t('text.enterStationIds')"
							:info-text="$t('text.specifyStationIdsLimitDestinations')"
						/>
					</div>
				</div>

				<!-- Toggle for additional restrictions -->
				<IfFeatureFlag flag="FEATURE_RIGI_PD_CHANGES">
					<div class="mb-4">
						<div class="d-flex align-center justify-space-between py-2">
							<div>
								<div class="font-weight-medium">
									{{ isOutwardJourney ? $t('text.wantToSetDepartureStationRestrictions') : $t('text.wantToSetArrivalStationRestrictions') }}
								</div>
								<div class="text-caption text--secondary">
									{{ isOutwardJourney ? $t('text.departureStationRestrictionsToggleDescription') : $t('text.arrivalStationRestrictionsToggleDescription') }}
								</div>
							</div>
							<SwitchField
								v-model="additionalRestrictionsEnabled"
								@change="onAdditionalRestrictionsToggle"
								:hideSwitchClear="true"
							/>
						</div>
					</div>

					<!-- Dynamic field - shown based on toggle state -->
					<div v-if="additionalRestrictionsEnabled" class="mb-4">
						<!-- For outward journey: add departure stations -->
						<div v-if="isOutwardJourney">
							<Field 
								typeName="AddedValue" 
								fieldName="departureStations" 
								v-model="modelValue.fields.departureStations"
								:placeholder="$t('text.enterStationIds')"
								:info-text="$t('text.specifyStationIdsLimitDestinations')"
							/>
						</div>
						
						<!-- For return journey: add arrival stations -->
						<div v-else>
							<Field 
								typeName="AddedValue" 
								fieldName="arrivalStations" 
								v-model="modelValue.fields.arrivalStations"
								:placeholder="$t('text.enterStationIds')"
								:info-text="$t('text.specifyStationIdsLimitDestinations')"
							/>
						</div>
					</div>
				</IfFeatureFlag>
			</FieldSet>
		</Optional>

		<!-- since this is not part of the model we cannot use the Field component for the model driven pattern -->
		<FieldSet v-if="isOutwardJourney && (modelValue.fields.arrivalStations?.de || (additionalRestrictionsEnabled && modelValue.fields.departureStations?.de))" id="copyToReturnJourney">
			<v-row no-gutters style="padding-right: 10px;">
				<v-col cols="6" class="d-flex justify-start">
					<p>{{ $t('text.copyToReturnJourneyHelp') }}</p>
				</v-col>
				<v-col cols="6" class="d-flex justify-end">
					<!-- TODO: replace with a button -->
					<v-btn @click="copyStationsForReturnJourney">{{ $t('text.copy') }}</v-btn>
				</v-col>
			</v-row>
		</FieldSet>

		<Optional id="showPrice" v-model="optionalSettings" :autoShow="!!modelValue.fields.showPrice?.de">
			<Field class="mb-4" typeName="AddedValue" fieldName="showPrice" v-model="modelValue.fields.showPrice" :infoText="$t('text.showPriceInfoText')" />
		</Optional>

		<Optional id="mandatory" v-model="optionalSettings" :autoShow="!!modelValue.fields.required?.de">
			<Field class="mb-4" typeName="AddedValue" fieldName="required" v-model="modelValue.fields.required" :infoText="$t('text.mandatoryInfoText')" :title="$t('text.mandatory')" />
		</Optional>

		<DataDialog v-model="optionalSettings" :title="$t('text.customizeOptions')" ref="optionalSettingsDialog" :useWrap="true">
			<template #content="{ wrap }">
				<!-- TODO: the model is wrong here, we need a separate model per item.
							probably this whole dialog should actually move into the itemHead instead.
				-->
				<OptionalSettings v-model="wrap.model" @update:modelValue="wrap['model'] = $event" :items="optionalSettingsItems" />
			</template>
		</DataDialog>
	</div>
</template>

<script>
import DataDialog from '../../../../components/common/DataDialog.vue'
import Field from '../../../../components/fields/Field.vue'
import SwitchField from '../../../../components/fields/SwitchField.vue'
import FieldSet from '../FieldSet.vue'
import Optional from '../Optional.vue'
import OptionalSettings from '../OptionalSettings.vue'
import IfFeatureFlag from '../../../../components/ifFeatureFlag/IfFeatureFlag.vue'
import eventBus from '@/utils/eventBus.js'
import EditableTable from '../EditableTable.vue'
import AddButton from '../AddButton.vue'

export default {
	components: { DataDialog, Optional, FieldSet, Field, SwitchField, OptionalSettings, IfFeatureFlag, EditableTable, AddButton },
	props: {
		modelValue: Object,
	},
	computed: {
		optionalSettingsItems() {
			return [
				{ id: 'arrivalAndDepartureRestrictions' },
				{ id: 'validitySettings' },
				{ id: 'additionalSettings', items: ['showPrice', 'mandatory'] },
			]
		},
		isOutwardJourney() {
			return this.modelValue.fields.transportType.de == 'outward'
		},
	},
	data: () => ({
		optionalSettings: {},
		additionalRestrictionsEnabled: false,
	}),
	methods: {
		// TODO: how can we copy the stations to the return journey when return journey is the same component for a different modelValue? The event bus does not seem to be working for this case
		copyStationsForReturnJourney() {
			// Copy both static and additional fields if they exist
			const stationsToCopy = {
				arrivalStations: this.modelValue.fields.arrivalStations,
				departureStations: this.modelValue.fields.departureStations,
				additionalRestrictionsEnabled: this.additionalRestrictionsEnabled
			}
			eventBus.$emit('copyStationsForReturnJourney', stationsToCopy)
		},
		addSeasonalDiscount() {
			const seasonalDiscount = this.seasonalDiscountFactory()
			this.modelValue.fields.seasonalDiscounts.de.push(seasonalDiscount)
		},
		seasonalDiscountFactory() {
			return {
				sys: { id: '' },
				fields: {
					startDate: { de: null },
					endDate: { de: null },
					reductionPercent: { de: 0 },
				},
			}
		},
		handleOptionClick(id, action) {
			if (action === 'delete') {
				const index = this.modelValue.fields.seasonalDiscounts.de.findIndex(item => item.sys.id === id)
				if (index >= 0) {
					this.modelValue.fields.seasonalDiscounts.de.splice(index, 1)
				}
			}
		},
		onAdditionalRestrictionsToggle() {
			// If toggle is disabled, clear the additional station fields
			if (!this.additionalRestrictionsEnabled) {
				// Clear departure stations for outward journey (additional field)
				if (this.isOutwardJourney && this.modelValue.fields.departureStations) {
					this.modelValue.fields.departureStations = null
				}
				// Clear arrival stations for return journey (additional field)
				else if (!this.isOutwardJourney && this.modelValue.fields.arrivalStations) {
					this.modelValue.fields.arrivalStations = null
				}
			}
		},
		initializeToggleState() {
			// Enable toggle if there are existing additional station restrictions
			const hasAdditionalStations = (this.isOutwardJourney && this.modelValue.fields.departureStations?.de?.length) ||
										 (!this.isOutwardJourney && this.modelValue.fields.arrivalStations?.de?.length)
			this.additionalRestrictionsEnabled = !!hasAdditionalStations
		},
	},
	mounted() {
		console.log(this.modelValue)
	},
	created() {
		eventBus.$on('copyStationsForReturnJourney', (stationData) => {
			if (!this.isOutwardJourney && stationData) {
				// For return journey: copy arrival to departure (static field) and departure to arrival (additional field)
				if (stationData.arrivalStations) {
					this.modelValue.fields.departureStations = stationData.arrivalStations
				}
				if (stationData.departureStations) {
					this.modelValue.fields.arrivalStations = stationData.departureStations
					this.additionalRestrictionsEnabled = true
				}
				else {
					this.additionalRestrictionsEnabled = stationData.additionalRestrictionsEnabled || false
				}
			}
		})
		
		// Initialize toggle state based on existing data
		this.initializeToggleState()
	},
	beforeUnmount() {
		eventBus.$off('copyStationsForReturnJourney')
	},
}
</script>

<style scoped>
.optional-items > * {
	padding-bottom: 20px;
}
</style>